// Staged document actions (spec/dashboard.md section 6.1): clone (owned components cloned too), replace-by (one edit per
// referencing document), the filter bar's rules as the server's `filter`.
import type { Document, Json, Parameter, Template } from '../api/types';
import type { FilterRule } from '../ui/FilterBar';
import { entityKey, isTempId, type Workspace } from '../workspace/Workspace';
import type { ContentCatalog } from './ContentContext';
import type { DocumentLike } from './format';
import { isComponentTarget } from './widgets/context';

export function baseOf(doc: Document): { version: number | null; value: Json } {
  return { version: isTempId(doc.id) ? null : Number(doc.version), value: doc as unknown as Json };
}

/** A staged copy of a document: the same values and tags; the components it owns are cloned and re-pointed, so no clone shares a component. */
export function cloneDocument(workspace: Workspace, catalog: ContentCatalog, doc: DocumentLike, depth = 0): string {
  const template = catalog.templates.get(doc.template);
  const data = { ...((doc.data ?? {}) as Record<string, Json>) };
  if (template && depth < 4) {
    for (const p of template.effectiveParameters) {
      const value = data[p.name];
      if (p.type === 'document' && isComponentTarget(catalog, p) && typeof value === 'string') {
        const owned = catalog.resolve(value);
        if (owned) data[p.name] = cloneDocument(workspace, catalog, owned, depth + 1);
      } else if (p.type === 'list' && p.of?.type === 'document' && isComponentTarget(catalog, p.of) && Array.isArray(value)) {
        data[p.name] = value.map((v) => {
          const owned = typeof v === 'string' ? catalog.resolve(v) : undefined;
          return owned ? cloneDocument(workspace, catalog, owned, depth + 1) : v;
        });
      }
    }
  }
  const source = doc as Partial<Document>;
  const fresh = { id: '', unnyId: null, template: doc.template, export: source.export ?? true, tags: [...(source.tags ?? [])], legacyFlags: 0, data, legacy: null, meta: source.meta ?? null, version: 0 } as unknown as Json;
  const label = `Copy of ${catalog.label(doc)}`;
  const id = workspace.create('document', fresh, { label, template: doc.template });
  return id;
}

/** Replace-by: every reference to `from` re-pointed at `to` as one staged edit per referencing document; returns the documents touched. */
export function stageReplaceBy(workspace: Workspace, catalog: ContentCatalog, references: { document: Document; path: string }[], from: string, to: string): number {
  const touched = new Set<string>();
  for (const { document, path } of references) {
    const existing = workspace.getDraft(entityKey('document', document.id));
    const current = (existing?.current as unknown as Document | undefined) ?? document;
    const at = `data.${path}`;
    workspace.edit('document', document.id, at, to, { base: baseOf(document), label: catalog.label(current), template: document.template });
    touched.add(document.id);
  }
  void from;
  return touched.size;
}

const SERVER_OP: Record<FilterRule['op'], string> = { eq: 'eq', ne: 'ne', gt: 'gt', ge: 'gte', lt: 'lt', le: 'lte', contains: 'contains', starts: 'startsWith', empty: 'empty', notEmpty: 'notEmpty' };

/** The filter bar's rules as the list's `filter` (section 20.2); rules the server cannot see (`_dirty`) are left to the page. */
export function toServerFilter(rules: FilterRule[], params: Parameter[]): { field: string; op: string; value?: Json }[] {
  const out: { field: string; op: string; value?: Json }[] = [];
  for (const r of rules) {
    if (r.field.startsWith('_')) continue;
    const op = SERVER_OP[r.op];
    const param = r.field.startsWith('data.') ? params.find((p) => `data.${p.name}` === r.field) : undefined;
    let value: Json = r.value;
    if (param && (param.type === 'int' || param.type === 'float' || param.type === 'duration' || param.type === 'date_time' || param.type === 'enum')) value = Number(r.value);
    else if (param?.type === 'long') value = r.value;
    else if (param?.type === 'bool' || r.field === 'export') value = r.value === 'true';
    else if (r.field === 'version') value = Number(r.value);
    if (op === 'empty' || op === 'notEmpty') out.push({ field: r.field, op });
    else out.push({ field: r.field, op, value });
  }
  return out;
}

/** The heirs of a template, nearest first, then every other concrete document template (the change-template choices). */
export function changeTemplateChoices(catalog: ContentCatalog, from: string): { heirs: Template[]; others: Template[] } {
  const isHeir = (t: Template) => {
    let p: string | null | undefined = t.parent;
    const seen = new Set<string>();
    while (p && !seen.has(p)) {
      if (p === from) return true;
      seen.add(p);
      p = catalog.templates.get(p)?.parent;
    }
    return false;
  };
  const concrete = catalog.templateList.filter((t) => t.name !== from && (t.kind === 'document' || t.kind === 'singleton' || t.kind === 'component') && !t.flags.includes('abstract'));
  return { heirs: concrete.filter(isHeir), others: concrete.filter((t) => !isHeir(t)) };
}
